import { useCallback, useEffect } from 'react';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { useEventListener } from '@trinity/chat-components/hooks/useEventListener';
import { cx } from '@trinity/chat-components/utils';
import { useScrollLock } from './hooks/useScrollLock';
import styles from './style/Base.module.css';

type Props = {
  classes?: {
    root?: string;
    wrap?: string;
    close?: string;
  };
  children: React.ReactNode;
  disableEscapeClose?: boolean;
  disableOverlayClick?: boolean;
  disablePortal?: boolean;
  disableEventPropagation?: boolean;
  onClose?: () => unknown;
  open: boolean;
};

const ModalBase = ({ classes = {}, open, disableEscapeClose = false, disableOverlayClick = false, disableEventPropagation = false, disablePortal = false, onClose, ...props }: Props) => {
  const scroll = useScrollLock(open);

  const handleScrollLock = useCallback(() => {
    if (open) {
      scroll.lock();
    } else {
      scroll.unlock();
    }
  }, [
    open,
    scroll,
  ]);

  const handleClose = useCallback(() => {
    if (!disableOverlayClick) {
      onClose?.();
    }
  }, [disableOverlayClick, onClose]);

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (!open) return;

    switch (e.key) {
      case 'Escape': {
        if (!disableEscapeClose) {
          handleClose();
        }
        break;
      }
    }
  }, [disableEscapeClose, open, handleClose]);

  useEventListener('keydown', handleKeydown);

  const cancelPropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    handleScrollLock();
  }, [
    handleScrollLock,
  ]);

  if (!open) return null;

  return (
    <Portal disablePortal={disablePortal}>
      <div className={styles.root} onClick={disableEventPropagation ? cancelPropagation : null}>
        <div className={cx(styles.wrap, classes.root)}>
          <div className={cx(styles.main, classes.wrap)}>
            {props.children}
          </div>
        </div>
        <div
          className={styles.overlay}
          onClick={handleClose} />
      </div>
    </Portal>
  );
};

export type ModalBaseProps = Props;

export { ModalBase };
export default ModalBase;